import React, { useRef, useState, useEffect, useCallback } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  IconButton,
  Divider,
  Slider,
  Checkbox,
  TextField,
  Button
} from "@mui/material";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import BreatheRight from "../assets/BreatheRight.png";
import { submitCustomerFeedback } from "../services/customerFeedbackService";
import { markSessionMusicCompleted } from "../services/customerSessionDetails";

const pulseGlow = keyframes`
    0%, 100% {
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
        transform: scale(1);
    }
    50% {
        box-shadow: 0 0 40px rgba(255, 255, 255, 0.4);
        transform: scale(1.05);
    }
`;

// Display names for supported language codes. Add more entries here as more
// languages are supported (must match the codes coming from patient creation).
const languageMap = {
  eng: "English",
  kan: "Kannada",
  hin: "Hindi",
};

// Backend/patient-creation systems don't always agree on how a language is
// spelled ("Hindi", "hindi", "hi", "hin", etc). This maps any of those
// variants onto the 3-letter codes used by `languageMap` / `translations`
// above, so the toggle button and text always resolve correctly.
const LANG_CODE_ALIASES = {
  english: "eng",
  en: "eng",
  eng: "eng",
  kannada: "kan",
  kn: "kan",
  kan: "kan",
  hindi: "hin",
  hi: "hin",
  hin: "hin",
};

const normalizeLangCode = (code) => {
  if (!code) return null;
  const key = String(code).trim().toLowerCase();
  return LANG_CODE_ALIASES[key] || key;
};

// All user-facing strings for the two screens in this component, keyed by
// language code, then by a stable, language-independent key.
const translations = {
  eng: {
    // Player screen
    musicTherapy: "Music Therapy",
    defaultSubtitle: "Healing begins with hope, rest, and support.",

    // Session complete screen
    sessionComplete: "Session Complete!",
    shareFeeling: "Please share how you're feeling after the therapy",
    howDoYouFeel: "How do you feel?",
    currentPainLevel: "Current Pain Level",
    moodQuestion: "How's your mood now?",
    relaxationLevelTitle: "Current Relaxation Level",
    therapySessionFeedback: "Therapy Session Feedback",
    effectivenessQuestion: "How effective was this therapy session?",
    experienceQuestion:
      "Did you experience any of these during the session? (Select all that apply)",
    continueQuestion: "Would you like to continue with this type of therapy?",
    commentsLabel: "Additional Comments (optional)",
    commentsPlaceholder: "Write anything you'd like to share...",
    submitFeedback: "Submit Feedback",
    submitSuccess: "Thank you! Your feedback has been submitted.",
    submitError: "Something went wrong while submitting feedback.",

    mood: {
      very_poor: "Very Poor",
      poor: "Poor",
      neutral: "Neutral",
      good: "Good",
      very_good: "Very Good",
    },
    relaxation: {
      very_tense: "Very Tense",
      tense: "Tense",
      neutral: "Neutral",
      relaxed: "Relaxed",
      very_relaxed: "Very Relaxed",
    },
    effectiveness: {
      not_effective: "Not Effective",
      slightly_effective: "Slightly Effective",
      moderately_effective: "Moderately Effective",
      very_effective: "Very Effective",
      extremely_effective: "Extremely Effective",
    },
    experience: {
      deep_relaxation: "Deep Relaxation",
      pain_relief: "Pain Relief",
      improved_mood: "Improved Mood",
      reduced_anxiety: "Reduced Anxiety",
      better_focus: "Better Focus",
      drowsiness: "Drowsiness",
      no_change: "No Noticeable Change",
      discomfort: "Discomfort",
    },
    continue: {
      no: "No",
      maybe: "Maybe",
      yes: "Yes",
    },
  },
  kan: {
    // Player screen
    musicTherapy: "ಸಂಗೀತ ಚಿಕಿತ್ಸೆ",
    defaultSubtitle: "ಗುಣಮುಖವಾಗುವಿಕೆ ಆಶಾ, ವಿಶ್ರಾಂತಿ ಮತ್ತು ಬೆಂಬಲದೊಂದಿಗೆ ಪ್ರಾರಂಭವಾಗುತ್ತದೆ.",

    // Session complete screen
    sessionComplete: "ಅಧಿವೇಶನ ಪೂರ್ಣಗೊಂಡಿದೆ!",
    shareFeeling: "ಚಿಕಿತ್ಸೆಯ ನಂತರ ನೀವು ಹೇಗೆ ಅನುಭವಿಸುತ್ತಿದ್ದೀರಿ ಎಂಬುದನ್ನು ದಯವಿಟ್ಟು ಹಂಚಿಕೊಳ್ಳಿ",
    howDoYouFeel: "ನಿಮಗೆ ಹೇಗನಿಸುತ್ತಿದೆ?",
    currentPainLevel: "ಪ್ರಸ್ತುತ ನೋವಿನ ಮಟ್ಟ",
    moodQuestion: "ಈಗ ನಿಮ್ಮ ಮನಸ್ಥಿತಿ ಹೇಗಿದೆ?",
    relaxationLevelTitle: "ಪ್ರಸ್ತುತ ವಿಶ್ರಾಂತಿಯ ಮಟ್ಟ",
    therapySessionFeedback: "ಚಿಕಿತ್ಸಾ ಅಧಿವೇಶನದ ಪ್ರತಿಕ್ರಿಯೆ",
    effectivenessQuestion: "ಈ ಚಿಕಿತ್ಸಾ ಅಧಿವೇಶನ ಎಷ್ಟು ಪರಿಣಾಮಕಾರಿಯಾಗಿತ್ತು?",
    experienceQuestion:
      "ಅಧಿವೇಶನದ ಸಮಯದಲ್ಲಿ ನೀವು ಇವುಗಳಲ್ಲಿ ಯಾವುದನ್ನಾದರೂ ಅನುಭವಿಸಿದ್ದೀರಾ? (ಅನ್ವಯಿಸುವ ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ)",
    continueQuestion: "ಈ ರೀತಿಯ ಚಿಕಿತ್ಸೆಯನ್ನು ಮುಂದುವರಿಸಲು ನೀವು ಬಯಸುವಿರಾ?",
    commentsLabel: "ಹೆಚ್ಚುವರಿ ಟಿಪ್ಪಣಿಗಳು (ಐಚ್ಛಿಕ)",
    commentsPlaceholder: "ನೀವು ಹಂಚಿಕೊಳ್ಳಲು ಬಯಸುವುದನ್ನು ಬರೆಯಿರಿ...",
    submitFeedback: "ಪ್ರತಿಕ್ರಿಯೆ ಸಲ್ಲಿಸಿ",
    submitSuccess: "ಧನ್ಯವಾದಗಳು! ನಿಮ್ಮ ಪ್ರತಿಕ್ರಿಯೆ ಸಲ್ಲಿಸಲಾಗಿದೆ.",
    submitError: "ಪ್ರತಿಕ್ರಿಯೆ ಸಲ್ಲಿಸುವಾಗ ಏನೋ ತಪ್ಪಾಗಿದೆ.",

    mood: {
      very_poor: "ತುಂಬಾ ಕಳಪೆ",
      poor: "ಕಳಪೆ",
      neutral: "ತಟಸ್ಥ",
      good: "ಚೆನ್ನಾಗಿದೆ",
      very_good: "ತುಂಬಾ ಚೆನ್ನಾಗಿದೆ",
    },
    relaxation: {
      very_tense: "ತುಂಬಾ ಒತ್ತಡ",
      tense: "ಒತ್ತಡ",
      neutral: "ತಟಸ್ಥ",
      relaxed: "ವಿಶ್ರಾಂತವಾಗಿದೆ",
      very_relaxed: "ತುಂಬಾ ವಿಶ್ರಾಂತವಾಗಿದೆ",
    },
    effectiveness: {
      not_effective: "ಪರಿಣಾಮಕಾರಿಯಲ್ಲ",
      slightly_effective: "ಸ್ವಲ್ಪ ಪರಿಣಾಮಕಾರಿ",
      moderately_effective: "ಮಧ್ಯಮ ಪರಿಣಾಮಕಾರಿ",
      very_effective: "ತುಂಬಾ ಪರಿಣಾಮಕಾರಿ",
      extremely_effective: "ಅತ್ಯಂತ ಪರಿಣಾಮಕಾರಿ",
    },
    experience: {
      deep_relaxation: "ಆಳವಾದ ವಿಶ್ರಾಂತಿ",
      pain_relief: "ನೋವು ಪರಿಹಾರ",
      improved_mood: "ಸುಧಾರಿತ ಮನಸ್ಥಿತಿ",
      reduced_anxiety: "ಕಡಿಮೆಯಾದ ಆತಂಕ",
      better_focus: "ಉತ್ತಮ ಏಕಾಗ್ರತೆ",
      drowsiness: "ಜೊಂಪು / ನಿದ್ರಾಳುತನ",
      no_change: "ಗಮನಾರ್ಹ ಬದಲಾವಣೆ ಇಲ್ಲ",
      discomfort: "ಅಸ್ವಸ್ಥತೆ",
    },
    continue: {
      no: "ಇಲ್ಲ",
      maybe: "ಬಹುಶಃ",
      yes: "ಹೌದು",
    },
  },
  hin: {
    // Player screen
    musicTherapy: "संगीत चिकित्सा",
    defaultSubtitle: "उपचार आशा, विश्राम और सहयोग से शुरू होता है।",

    // Session complete screen
    sessionComplete: "सत्र पूर्ण हुआ!",
    shareFeeling: "कृपया बताएं कि चिकित्सा के बाद आप कैसा महसूस कर रहे हैं",
    howDoYouFeel: "आपको कैसा महसूस हो रहा है?",
    currentPainLevel: "वर्तमान दर्द स्तर",
    moodQuestion: "अभी आपका मूड कैसा है?",
    relaxationLevelTitle: "वर्तमान विश्राम स्तर",
    therapySessionFeedback: "चिकित्सा सत्र प्रतिक्रिया",
    effectivenessQuestion: "यह चिकित्सा सत्र कितना प्रभावी था?",
    experienceQuestion:
      "क्या आपने सत्र के दौरान इनमें से कोई अनुभव किया? (लागू होने वाले सभी का चयन करें)",
    continueQuestion: "क्या आप इस प्रकार की चिकित्सा जारी रखना चाहेंगे?",
    commentsLabel: "अतिरिक्त टिप्पणियाँ (वैकल्पिक)",
    commentsPlaceholder: "आप जो कुछ भी साझा करना चाहें लिखें...",
    submitFeedback: "प्रतिक्रिया सबमिट करें",
    submitSuccess: "धन्यवाद! आपकी प्रतिक्रिया सबमिट कर दी गई है।",
    submitError: "प्रतिक्रिया सबमिट करते समय कुछ गलत हो गया।",

    mood: {
      very_poor: "बहुत खराब",
      poor: "खराब",
      neutral: "सामान्य",
      good: "अच्छा",
      very_good: "बहुत अच्छा",
    },
    relaxation: {
      very_tense: "बहुत तनावग्रस्त",
      tense: "तनावग्रस्त",
      neutral: "सामान्य",
      relaxed: "शांत",
      very_relaxed: "बहुत शांत",
    },
    effectiveness: {
      not_effective: "प्रभावी नहीं",
      slightly_effective: "थोड़ा प्रभावी",
      moderately_effective: "मध्यम प्रभावी",
      very_effective: "बहुत प्रभावी",
      extremely_effective: "अत्यंत प्रभावी",
    },
    experience: {
      deep_relaxation: "गहरा विश्राम",
      pain_relief: "दर्द से राहत",
      improved_mood: "बेहतर मूड",
      reduced_anxiety: "कम चिंता",
      better_focus: "बेहतर ध्यान",
      drowsiness: "नींद / सुस्ती",
      no_change: "कोई उल्लेखनीय बदलाव नहीं",
      discomfort: "असुविधा",
    },
    continue: {
      no: "नहीं",
      maybe: "शायद",
      yes: "हाँ",
    },
  },
};

// Canonical (English) option lists. `key` is the language-independent value
// that gets stored in state and submitted to the backend, so the payload
// stays identical no matter which language is being displayed on screen.
const MOOD_OPTIONS = [
  { emoji: "😣", key: "very_poor" },
  { emoji: "☹️", key: "poor" },
  { emoji: "😐", key: "neutral" },
  { emoji: "🙂", key: "good" },
  { emoji: "😄", key: "very_good" },
];

const RELAXATION_OPTIONS = [
  { emoji: "😣", key: "very_tense" },
  { emoji: "🙁", key: "tense" },
  { emoji: "😐", key: "neutral" },
  { emoji: "🙂", key: "relaxed" },
  { emoji: "😌", key: "very_relaxed" },
];

const EFFECTIVENESS_OPTIONS = [
  { emoji: "😑", key: "not_effective" },
  { emoji: "🙂", key: "slightly_effective" },
  { emoji: "😊", key: "moderately_effective" },
  { emoji: "😄", key: "very_effective" },
  { emoji: "🤩", key: "extremely_effective" },
];

const EXPERIENCE_OPTIONS = [
  "deep_relaxation",
  "pain_relief",
  "improved_mood",
  "reduced_anxiety",
  "better_focus",
  "drowsiness",
  "no_change",
  "discomfort",
];

const CONTINUE_OPTIONS = ["no", "maybe", "yes"];

const MusicTherapy = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const storedCustomer = JSON.parse(localStorage?.getItem("customer") || "{}");

  const song = storedCustomer?.track_url
    ? (() => {
      let src = storedCustomer?.track_url

      console.log("🎵 Song path:", src);

      return {
        title: storedCustomer?.track_name || "Unknown Track",
        src,
        theme: "theme1",
        icon: BreatheRight,
      };
    })()
    : null;

  // ----------------------- LANGUAGE -----------------------
  // The patient's preferred language is captured at patient-creation time.
  // TODO: once that payload/field name is finalized, replace this fallback
  // chain with the confirmed source (e.g. storedCustomer.preferred_language).
  const rawPreferredLang =
    storedCustomer?.preferred_language ||
    storedCustomer?.preferredLang ||
    storedCustomer?.language ||
    localStorage.getItem("preferredLang") ||
    "kan";

  // Normalize whatever the backend sent ("Hindi", "hi", "hin", ...) into one
  // of our supported codes. If it's a language we don't have translations
  // for yet, fall back to English rather than showing an empty button/text.
  const normalizedPreferredLang = normalizeLangCode(rawPreferredLang);
  const preferredLang = translations[normalizedPreferredLang]
    ? normalizedPreferredLang
    : "eng";

  const [currentLang, setCurrentLang] = useState(preferredLang);

  const handleToggle = () => {
    setCurrentLang((prev) => (prev === "eng" ? preferredLang : "eng"));
  };

  const t = translations[currentLang] || translations.eng;

  const renderLanguageToggle = (sx = {}) =>
    preferredLang !== "eng" && (
      <Button
        variant="contained"
        onClick={handleToggle}
        sx={{
          bgcolor: "#1976d2",
          "&:hover": { bgcolor: "#1565c0" },
          borderRadius: 2,
          px: { xs: 1.8, sm: 2.5 },
          py: { xs: 0.9, sm: 1 },
          textTransform: "none",
          fontWeight: 600,
          fontSize: { xs: "0.8rem", sm: "0.875rem" },
          whiteSpace: "nowrap",
          ...sx,
        }}
      >
        {currentLang === "eng" ? languageMap[preferredLang] : "English"}
      </Button>
    );

  // Core player state (keeps re-renders minimal)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [volume, setVolume] = useState(1);
  const [favorites, setFavorites] = useState([]);
  const [sessionComplete, setSessionComplete] = useState(() => {
    return localStorage?.getItem("sessionComplete") === "true";
  });
  const [painScale, setPainScale] = useState();
  const [selectedExperiences, setSelectedExperiences] = useState([]);
  const [therapyContinue, setTherapyContinue] = useState(null);
  const [comments, setComments] = useState("");
  const [selectedMood, setSelectedMood] = useState("");
  const [selectedRelaxation, setSelectedRelaxation] = useState("");
  const [selectedEffectiveness, setSelectedEffectiveness] = useState("");

  // refs for audio + UI elements we mutate directly
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const currentTimeRef = useRef(null);
  const durationRef = useRef(null);
  const rafRef = useRef(null);
  const durationValueRef = useRef(0); // store duration (seconds) in ref
  const isMountedRef = useRef(false);

  // animation elements state (kept as array state since changes are infrequent)
  const [animationElements, setAnimationElements] = useState([]);

  const playNext = useCallback(() => {
    // Only one song → always index 0
    setCurrentIndex(0);
    setIsPlaying(true);
  }, [isShuffle]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      cancelAnimationFrame(rafRef.current);
      setIsPlaying(false);
    } else {
      audio.play().catch(() => { });
      startProgressLoop(); // ensure RAF loop running
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Utility: format seconds to M:SS
  const formatTime = (seconds = 0) => {
    if (!seconds || Number.isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // Update audio src when currentIndex changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // set src and load
    audio.src = song?.src;
    audio.load();

    // apply volume
    audio.volume = volume;

    // if should play, attempt to play
    if (isPlaying) {
      audio
        .play()
        .then(() => {
          // started
          startProgressLoop();
        })
        .catch(() => {
          // autoplay blocked — UI still shows paused until user clicks
        });
    } else {
      // ensure we reset UI if paused
      cancelAnimationFrame(rafRef.current);
      // set progress UI to 0 if new track and not playing
      if (progressRef.current) progressRef.current.style.width = "0%";
      if (currentTimeRef.current) currentTimeRef.current.textContent = "0:00";
      if (durationRef.current) durationRef.current.textContent = song.time || "0:00";
      durationValueRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]); // deliberately not including isPlaying/volume here to decouple

  // When volume state changes update audio element
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // onLoadedMetadata: set duration ref
  const onLoadedMetadata = (e) => {
    const audio = e.target;
    const dur = audio.duration || 0;
    durationValueRef.current = dur;
    if (durationRef.current) durationRef.current.textContent = formatTime(dur);
  };

  // useEffect(() => {
  //   const saved = localStorage.getItem("sessionComplete");
  //   if (saved === "true") {
  //     setSessionComplete(true);
  //   }
  // }, []);

  // onEnded: handle repeat or next
  const onEnded = () => {
    if (isRepeat) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => { });
      }
    } else {
      setIsPlaying(false);
      setSessionComplete(true);
      localStorage.setItem("sessionComplete", "true");

      const updatedCustomer = JSON.parse(localStorage.getItem("customer") || "{}");
      if (updatedCustomer.customer_session_id) {
        updatedCustomer.music_completed = true;
        localStorage.setItem("customer", JSON.stringify(updatedCustomer));
        markSessionMusicCompleted(updatedCustomer.customer_session_id).catch((err) =>
          console.error("Failed to mark session complete:", err)
        );
      }
    }
  };

  // Start requestAnimationFrame loop to update UI (progress bar and current time) without setState per frame
  const startProgressLoop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const loop = () => {
      const dur = durationValueRef.current || audio.duration || 0;
      const cur = audio.currentTime || 0;
      // update progress bar width (read-only)
      if (progressRef.current) {
        const pct = dur ? (cur / dur) * 100 : 0;
        progressRef.current.style.width = `${pct}%`;
      }
      // update current time label
      if (currentTimeRef.current) currentTimeRef.current.textContent = formatTime(cur);

      rafRef.current = requestAnimationFrame(loop);
    };

    // ensure any previous RAF canceled
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  // Clean up RAF on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Attach audio listeners once (loadedmetadata + ended)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // When play state toggles to true, ensure progress loop runs
  useEffect(() => {
    if (isPlaying) startProgressLoop();
    else cancelAnimationFrame(rafRef.current);
  }, [isPlaying, startProgressLoop]);

  // Background gradient generator (kept from your original)
  const getBackgroundGradient = () => {
    // if (!isPlaying) {
    //   return "linear-gradient(145deg, #4fc3f7 0%, #0277bd 100%)";
    // }

    const currentTheme = song?.theme;
    switch (currentTheme) {
      case "theme1":
        return "linear-gradient(145deg, #80cbc4 0%, #00695c 100%)";
      case "theme2":
        return "linear-gradient(145deg, #f48fb1 0%, #c2185b 100%)";
      case "theme3":
        return "linear-gradient(135deg, #a855f7 0%, #9333ea 25%, #7c3aed 50%, #6d28d9 75%, #5b21b6 100%)";
      default:
        return "linear-gradient(145deg, #4fc3f7 0%, #0277bd 100%)";
    }
  };

  const isDisabled =
    !painScale ||
    selectedExperiences.length === 0 ||
    therapyContinue === null ||
    !selectedMood ||
    !selectedRelaxation ||
    !selectedEffectiveness;


  const handleSubmitFeedback = async () => {
    try {
      // Canonical English labels are submitted regardless of the language
      // currently shown on screen, so the backend payload never changes shape.
      const payload = {
        session_detail_id: storedCustomer?.customer_session_id, // 🔥 replace with dynamic ID later
        pain_level: Number(painScale),
        mood: translations.eng.mood[selectedMood],
        relaxation_level: translations.eng.relaxation[selectedRelaxation],
        session_feedback: translations.eng.effectiveness[selectedEffectiveness],
        experience: selectedExperiences.map((key) => translations.eng.experience[key]),
        interested: translations.eng.continue[therapyContinue],
        comments: comments
      };

      console.log("Submitting Payload:", payload);

      const response = await submitCustomerFeedback(payload);

      console.log("Feedback submitted successfully:", response);
      alert(t.submitSuccess);
      navigate("/my-profile");

    } catch (error) {
      console.error("Submit Error:", error);
      alert(t.submitError);
    }
  };

return sessionComplete ? (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      p: { xs: 2, sm: 3, md: 4 },
      borderRadius: 4,
      position: "relative",
      overflowX: "hidden",
      width: "100%",
    }}
  >
    {/* ----------------------- LANGUAGE TOGGLE ----------------------- */}
    <Box sx={{ position: "absolute", top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, zIndex: 5 }}>
      {renderLanguageToggle()}
    </Box>

    {/* ----------------------- HEADER ----------------------- */}
    <Typography
      variant="h4"
      sx={{ mb: 1, mt: { xs: 4, sm: 0 }, fontWeight: 700, fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.125rem" } }}
    >
      {t.sessionComplete}
    </Typography>

    <Typography
      variant="subtitle1"
      sx={{ mb: 1, fontSize: { xs: "0.85rem", sm: "1rem" }, px: { xs: 1, sm: 0 } }}
    >
      {t.shareFeeling}
    </Typography>

    <Typography variant="h6" sx={{ mb: 4, mt: 2, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
      {t.howDoYouFeel}
    </Typography>

    {/* ----------------------- PAIN SCALE ----------------------- */}
    <Box sx={{ mb: 4, width: "100%", maxWidth: 1000, px: { xs: 1, sm: 0 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#374151",
            fontSize: { xs: "0.9rem", sm: "1.05rem" },
          }}
        >
          {t.currentPainLevel}
        </Typography>

        <Box
          sx={{
            bgcolor: painScale
              ? `hsl(${120 - painScale * 12}, 70%, 50%)`
              : "#6b7280",
            color: "white",
            px: 2.2,
            py: 0.7,
            borderRadius: "12px",
            fontWeight: 700,
            fontSize: "1.15rem",
            minWidth: "52px",
            textAlign: "center",
            boxShadow: painScale
              ? `0 4px 12px hsla(${120 - painScale * 12}, 70%, 50%, 0.44)`
              : "none",
            transition: "all 0.3s ease",
          }}
        >
          {painScale ?? 0}
        </Box>
      </Box>

      <Slider
        value={Number(painScale) || 0}
        onChange={(e, value) => setPainScale(value)}
        min={0}
        max={10}
        step={1}
        marks={[
          { value: 0, label: "0" },
          { value: 2, label: "2" },
          { value: 4, label: "4" },
          { value: 6, label: "6" },
          { value: 8, label: "8" },
          { value: 10, label: "10" },
        ]}
      />
    </Box>

    {/* ----------------------- MOOD ----------------------- */}
    <Typography
      variant="h6"
      sx={{
        mb: 2,
        opacity: 0.9,
        textAlign: "left",
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        fontSize: { xs: "0.95rem", sm: "1.25rem" },
      }}
    >
      {t.moodQuestion}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)" },
        gap: { xs: 1, sm: 2 },
        mb: 3,
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
      }}
    >
      {MOOD_OPTIONS.map((item) => (
        <Card
          key={item.key}
          onClick={() => setSelectedMood(item.key)}
          sx={{
            p: { xs: 1, sm: 1.5 },
            minHeight: { xs: 70, sm: 80 },
            width: "100%",
            borderRadius: 3,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            bgcolor: "rgba(255,255,255,0.15)",
            border: selectedMood === item.key ? "2px solid #3b82f6" : "2px solid transparent",
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            transition: "all 0.2s",
          }}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }}>{item.emoji}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: "0.65rem", sm: "0.875rem" } }}>
            {t.mood[item.key]}
          </Typography>
        </Card>
      ))}
    </Box>

    {/* ----------------------- RELAXATION ----------------------- */}
    <Typography
      variant="h6"
      sx={{
        mb: 2,
        opacity: 0.9,
        textAlign: "left",
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        fontSize: { xs: "0.95rem", sm: "1.25rem" },
      }}
    >
      {t.relaxationLevelTitle}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)" },
        gap: { xs: 1, sm: 2 },
        mb: 3,
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
      }}
    >
      {RELAXATION_OPTIONS.map((item) => (
        <Card
          key={item.key}
          onClick={() => setSelectedRelaxation(item.key)}
          sx={{
            p: { xs: 1, sm: 1.5 },
            minHeight: { xs: 70, sm: 80 },
            width: "100%",
            borderRadius: 3,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            bgcolor: "rgba(255,255,255,0.15)",
            border: selectedRelaxation === item.key ? "2px solid #3b82f6" : "2px solid transparent",
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            transition: "all 0.2s",
          }}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }}>{item.emoji}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: "0.65rem", sm: "0.875rem" } }}>
            {t.relaxation[item.key]}
          </Typography>
        </Card>
      ))}
    </Box>

    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
      {t.therapySessionFeedback}
    </Typography>

    {/* ----------------------- EFFECTIVENESS ----------------------- */}
    <Typography
      variant="h6"
      sx={{
        mb: 2,
        opacity: 0.9,
        textAlign: "left",
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        fontSize: { xs: "0.95rem", sm: "1.25rem" },
      }}
    >
      {t.effectivenessQuestion}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)" },
        gap: { xs: 1, sm: 2 },
        mt: 1,
        mb: 4,
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
      }}
    >
      {EFFECTIVENESS_OPTIONS.map((item) => (
        <Card
          key={item.key}
          onClick={() => setSelectedEffectiveness(item.key)}
          sx={{
            p: { xs: 1, sm: 1.5 },
            minHeight: { xs: 70, sm: 80 },
            width: "100%",
            borderRadius: 3,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            bgcolor: "rgba(255,255,255,0.15)",
            border: selectedEffectiveness === item.key ? "2px solid #3b82f6" : "2px solid transparent",
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            transition: "all 0.2s",
          }}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }}>{item.emoji}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: "0.65rem", sm: "0.875rem" } }}>
            {t.effectiveness[item.key]}
          </Typography>
        </Card>
      ))}
    </Box>

    {/* ----------------------- EXPERIENCE ----------------------- */}
    <Typography
      variant="h6"
      sx={{
        mb: 2,
        opacity: 0.9,
        textAlign: "left",
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        fontSize: { xs: "0.95rem", sm: "1.25rem" },
      }}
    >
      {t.experienceQuestion}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gap: 2,
        mt: 1,
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
      }}
    >
      {EXPERIENCE_OPTIONS.map((key) => (
        <Card
          key={key}
          onClick={() =>
            setSelectedExperiences((prev) =>
              prev.includes(key)
                ? prev.filter((v) => v !== key)
                : [...prev, key]
            )
          }
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderRadius: 3,
            cursor: "pointer",
            bgcolor: "#f9fafb",
            border: selectedExperiences.includes(key)
              ? "2px solid #3b82f6"
              : "2px solid transparent",
            "&:hover": { bgcolor: "#f3f4f6" },
          }}
        >
          <Checkbox
            checked={selectedExperiences.includes(key)}
            sx={{
              color: selectedExperiences.includes(key) ? "#3b82f6" : "#6b7280",
              "&.Mui-checked": { color: "#3b82f6" },
            }}
          />
          <Typography variant="body1" sx={{ color: "#111827", fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            {t.experience[key]}
          </Typography>
        </Card>
      ))}
    </Box>

    {/* ----------------------- CONTINUE ----------------------- */}
    <Typography
      variant="h6"
      sx={{
        my: 4,
        opacity: 0.9,
        textAlign: "left",
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        fontSize: { xs: "0.95rem", sm: "1.25rem" },
      }}
    >
      {t.continueQuestion}
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        gap: 2,
        mb: 4,
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
      }}
    >
      {CONTINUE_OPTIONS.map((key) => (
        <Card
          key={key}
          onClick={() => setTherapyContinue(key)}
          sx={{
            px: 3,
            py: 2,
            width: "100%",
            borderRadius: 3,
            cursor: "pointer",
            textAlign: "center",
            bgcolor:
              therapyContinue === key
                ? "rgba(255,255,255,0.25)"
                : "rgba(255,255,255,0.15)",
            border:
              therapyContinue === key
                ? "2px solid #3b82f6"
                : "2px solid transparent",
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            transition: "all 0.2s",
          }}
        >
          <Typography variant="body1">{t.continue[key]}</Typography>
        </Card>
      ))}
    </Box>

    {/* ----------------------- COMMENTS ----------------------- */}
    <Typography
      variant="h6"
      sx={{
        mb: 2,
        opacity: 0.9,
        textAlign: "left",
        width: "100%",
        maxWidth: 1000,
        px: { xs: 1, sm: 0 },
        fontSize: { xs: "0.95rem", sm: "1.25rem" },
      }}
    >
      {t.commentsLabel}
    </Typography>

    <TextField
      multiline
      rows={4}
      placeholder={t.commentsPlaceholder}
      value={comments}
      onChange={(e) => setComments(e.target.value)}
      fullWidth
      sx={{
        width: "100%",
        maxWidth: 1000,
        mx: { xs: 1, sm: 0 },
        background: "#ffffff",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        "& .MuiOutlinedInput-root": {
          padding: 1,
          "& fieldset": { borderColor: "#d1d5db" },
          "&:hover fieldset": { borderColor: "#9ca3af" },
          "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 1.5 },
        },
        "& .MuiInputBase-input": { color: "#111827", fontSize: "0.95rem" },
        "& .MuiInputBase-input::placeholder": { color: "#6b7280", opacity: 1 },
      }}
    />

    <Button
      variant="contained"
      disabled={isDisabled}
      onClick={handleSubmitFeedback}
      sx={{
        mt: 4,
        mb: 6,
        px: { xs: 3, sm: 5 },
        py: 1.5,
        width: { xs: "100%", sm: "auto" },
        maxWidth: 1000,
        fontSize: { xs: "0.9rem", sm: "1rem" },
        borderRadius: 3,
        textTransform: "none",
      }}
    >
      {t.submitFeedback}
    </Button>
  </Card>
) : (
  // ...player branch unchanged
      <Card
        sx={{
          height: {
            xs: "calc(100vh - 56px)",
            sm: "calc(100vh - 64px)",
            md: "calc(100vh - 70px)",
          },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          background: getBackgroundGradient(),
          color: "#fff",
          borderRadius: 0,
          position: "relative",
          overflowX: "hidden",
          overflowY: "auto",
          transition: "background 1s ease-in-out",
        }}
      >
        {/* Animated Background Elements */}
        {animationElements.map((element) => (
          <Box key={element.id} sx={element.style} />
        ))}

        {/* Left Panel */}
        <Box
          sx={{
            width: { xs: "100%", md: "40%" },
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Header (top-left) */}
          <Box
            sx={{
              position: "absolute",
              top: { xs: 4, sm: 14, md: 16 },
              left: { xs: 4, sm: 14, md: 16 },
              zIndex: 5,
              ml: { xs: 2, md: 2 },
              pt: { xs: 2, md: 0 },
            }}
          >
            <Typography
              variant="h5"
              fontWeight={300}
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.5rem" },
              }}
            >
              {t.musicTherapy}
            </Typography>
          </Box>

          <CardContent
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
              mt: { xs: 12, sm: 14, md: 0 },
            }}
          >
            <Box
              sx={{
                width: { xs: "70%", md: "40vw" },
                height: { xs: 180, md: 220 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                mb: 2,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: { xs: "180px", sm: "200px", md: "220px" },
                  height: { xs: "180px", sm: "200px", md: "220px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                {[1, 2, 3, 4].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "absolute",
                      borderRadius: "50%",
                      border:
                        song?.theme === "theme1"
                          ? "2px solid rgba(192, 224, 204, 0.35)"
                          : song?.theme === "theme2"
                            ? "2px solid rgba(59, 130, 246, 0.35)"
                            : song?.theme === "theme3"
                              ? "2px solid rgba(168, 85, 247, 0.35)"
                              : "2px solid rgba(255, 255, 255, 0.22)",
                      width: `${index * 60}px`,
                      height: `${index * 60}px`,
                      animation: isPlaying ? `${pulseGlow} ${2 + index * 0.45}s ease-in-out infinite` : "none",
                      animationDelay: `${index * 0.25}s`,
                      opacity: 0.9,
                    }}
                  />
                ))}
              </Box>

              {/* Center Icon */}
              {song && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 3,
                    width: { xs: 60, sm: 70, md: 84 },
                    height: { xs: 60, sm: 70, md: 84 },
                    background:
                      song?.theme === "theme1"
                        ? "linear-gradient(135deg, rgba(34,197,94,0.32), rgba(34,197,94,0.16))"
                        : song?.theme === "theme2"
                          ? "linear-gradient(135deg, rgba(59,130,246,0.32), rgba(59,130,246,0.16))"
                          : song?.theme === "theme3"
                            ? "linear-gradient(135deg, rgba(168,85,247,0.32), rgba(168,85,247,0.16))"
                            : "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    backdropFilter: "blur(6px)",
                    pointerEvents: "auto",
                  }}
                >
                  <Box
                    component="img"
                    src={song?.icon}
                    alt={song?.title}
                    sx={{
                      width: { xs: 30, sm: 36, md: 48 },
                      height: { xs: 30, sm: 36, md: 48 },
                      objectFit: "contain",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
              )}
            </Box>

            <Typography
              variant="body1"
              sx={{
                opacity: 0.9,
                maxWidth: { xs: "92%", md: "80%" },
                mt: { xs: 4, md: 2 },
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              }}
            >
              {song?.title}
            </Typography>

          </CardContent>
        </Box>

        {/* Right Panel */}
        <CardContent
          sx={{
            width: { xs: "100%", md: "60%" },
            px: { xs: 2, sm: 3, md: 0 },
            py: { xs: 1, sm: 2, md: 0 },
            display: "flex",
            flexDirection: "column",
            mt: { xs: 0, sm: 0, md: 5 },
            marginLeft: { xs: 1 },
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",   // ⭐ vertical center
              height: "100%",             // ⭐ required
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                {storedCustomer?.track_displayName || t.defaultSubtitle}
              </Typography>
            </Box>


            {/* Music Controls */}
            <Box sx={{ mt: { xs: 2, md: 4 }, display: "flex", justifyContent: "center", width: "100%", pr: { xs: 2, sm: 4 }, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0, backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "28px", padding: "4px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}>

                <IconButton onClick={togglePlayPause} size="large" sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}>
                  {isPlaying ? <PauseCircleIcon sx={{ fontSize: 36 }} /> : <PlayCircleIcon sx={{ fontSize: 36 }} />}
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.2)", height: 24, alignSelf: "center" }} />

                <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                  <IconButton onClick={() => setShowSlider((p) => !p)} sx={{ color: volume === 0 ? "#aaa" : "#fff", mr: 1 }}>
                    <VolumeUpIcon />
                  </IconButton>

                  {/* Vertical Slider */}
                  {showSlider && (
                    <Box sx={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)", height: 120, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.5)", borderRadius: 2, p: 1, zIndex: 10 }}>
                      <Slider
                        orientation="vertical"
                        min={0}
                        max={1}
                        step={0.01}
                        value={volume}
                        onChange={(e, val) => {
                          setVolume(val);
                          if (audioRef.current) audioRef.current.volume = val;
                        }}
                        sx={{ height: 100, color: "#fff", "& .MuiSlider-thumb": { width: 12, height: 12 } }}
                      />
                    </Box>
                  )}
                </Box>

              </Box>
            </Box>

            {/* Audio element (hidden) */}
            <audio
              ref={audioRef}
              src={song?.src}
              preload="metadata"
              onLoadedMetadata={onLoadedMetadata}
              onEnded={onEnded}
              style={{ display: "none" }}
            />

            {/* Progress Row (read-only) */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {/* <Typography variant="caption" sx={{ minWidth: 35 }} ref={currentTimeRef}>
                0:00
              </Typography> */}

              <Box sx={{ flex: 1, height: 5, mx: 2, bgcolor: "rgba(255,255,255,0.3)", borderRadius: 5, position: "relative", cursor: "default", overflow: "hidden" }}>
                <Box ref={progressRef} sx={{ height: "100%", width: "0%", background: "linear-gradient(90deg, #667eea, #764ba2)", transition: "width 0.08s linear" }} />
              </Box>

              {/* <Typography variant="caption" sx={{ minWidth: 35 }} ref={durationRef}>
                {song.time || "0:00"}
              </Typography> */}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
};

export default MusicTherapy;
